import React, { useCallback, useEffect, useState } from "react";
import { Col, Container, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import { Button, SegmentedControl } from "../../components/ui";
import GetData from "../../services/getData";
import mailIcon from "../../media/history_transaction.png";
import "./smtpSettings.css";

/**
 * Страница «Настройки → Почта (SMTP)».
 *
 * Два таба:
 *   • Конфигурация — данные почтового сервера (хост, порт, шифрование,
 *     логин, пароль, отправитель). Пароль в интерфейс не возвращается:
 *     если он сохранён, поле пустое и показывает подсказку.
 *   • Журнал — история отправок из таблицы email_log: кто, кому, что
 *     отправил, вложение, статус и текст ошибки.
 */
const EMPTY_FORM = {
    host: "",
    port: "465",
    secure: "ssl",
    user: "",
    password: "",
    from_email: "",
    from_name: "",
};

const TABS = [
    { value: "config", labelKey: "smtp.tabs.config", fallback: "Конфигурация" },
    { value: "log", labelKey: "smtp.tabs.log", fallback: "Журнал" },
];

export default function SmtpSettings() {
    const { t } = useTranslation("settings");
    const [tab, setTab] = useState("config");

    const [form, setForm] = useState(EMPTY_FORM);
    const [hasPassword, setHasPassword] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [message, setMessage] = useState({ text: "", isError: false });

    const [emails, setEmails] = useState([]);
    const [emailsLoading, setEmailsLoading] = useState(true);

    const loadSettings = useCallback(async () => {
        setLoading(true);
        try {
            const data = await GetData({
                module: "settings",
                block: "smtp",
                action: "get",
            });
            const smtp = data?.smtp || {};
            setForm({
                host: smtp.host || "",
                port: smtp.port ? String(smtp.port) : "465",
                secure: smtp.secure || "ssl",
                user: smtp.user || "",
                password: "",
                from_email: smtp.from_email || "",
                from_name: smtp.from_name || "",
            });
            setHasPassword(Boolean(smtp.has_password));
        } catch (err) {
            console.error("Не удалось загрузить настройки SMTP:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadEmails = useCallback(async () => {
        setEmailsLoading(true);
        try {
            const data = await GetData({
                module: "patient",
                block: "send_email",
                action: "log",
                limit: 200,
            });
            setEmails(Array.isArray(data?.emails) ? data.emails : []);
        } catch (err) {
            console.error("Не удалось загрузить журнал писем:", err);
            setEmails([]);
        } finally {
            setEmailsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
        loadEmails();
    }, [loadSettings, loadEmails]);

    const update = (field) => (event) => {
        setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage({ text: "", isError: false });
        try {
            const data = await GetData({
                module: "settings",
                block: "smtp",
                action: "save",
                ...form,
            });
            if (data?.success === true) {
                setMessage({
                    text: data.message || t("smtp.messages.saved", "Настройки сохранены"),
                    isError: false,
                });
                setHasPassword(Boolean(data?.smtp?.has_password));
                setForm((prev) => ({ ...prev, password: "" }));
            } else {
                setMessage({
                    text: data?.message || t("smtp.messages.saveFailed", "Не удалось сохранить настройки"),
                    isError: true,
                });
            }
        } catch (err) {
            console.error("Ошибка сохранения SMTP:", err);
            setMessage({
                text: t("smtp.messages.saveFailed", "Не удалось сохранить настройки"),
                isError: true,
            });
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        setMessage({ text: "", isError: false });
        try {
            const data = await GetData({
                module: "settings",
                block: "smtp",
                action: "test",
            });
            setMessage({
                text:
                    data?.message ||
                    (data?.success
                        ? t("smtp.messages.testOk", "Письмо отправлено")
                        : t("smtp.messages.testFailed", "Ошибка отправки")),
                isError: data?.success !== true,
            });
            if (data?.success === true) loadEmails();
        } catch (err) {
            console.error("Ошибка проверки SMTP:", err);
            setMessage({
                text: t("smtp.messages.testFailed", "Ошибка отправки"),
                isError: true,
            });
        } finally {
            setTesting(false);
        }
    };

    return (
        <Container fluid="md">
            <Row>
                <Col>
                    <BlockTitle
                        title={
                            <TitleWithIcon
                                icon={mailIcon}
                                title={t("smtp.title", "Почта (SMTP)")}
                                showStatus={false}
                                status=""
                            />
                        }
                        buttons={[]}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="page">
                        <div className="page-content">
                            <div className="page-block">
                                <div className="smtp-tabs">
                                    <SegmentedControl
                                        ariaLabel={t("smtp.title", "Почта (SMTP)")}
                                        items={TABS.map((item) => ({
                                            value: item.value,
                                            label: t(item.labelKey, item.fallback),
                                        }))}
                                        onChange={setTab}
                                        value={tab}
                                    />
                                </div>

                                {message.text ? (
                                    <div
                                        className={message.isError ? "text-danger" : "text-success"}
                                        style={{ margin: "0.85rem 0" }}>
                                        {message.text}
                                    </div>
                                ) : null}

                                {tab === "config" ? (
                                    <SmtpConfigForm
                                        form={form}
                                        hasPassword={hasPassword}
                                        loading={loading}
                                        onTest={handleTest}
                                        onUpdate={update}
                                        onSubmit={handleSave}
                                        saving={saving}
                                        t={t}
                                        testing={testing}
                                    />
                                ) : (
                                    <SmtpLog emails={emails} loading={emailsLoading} onRefresh={loadEmails} t={t} />
                                )}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

/** Таб «Конфигурация»: параметры SMTP-сервера. */
function SmtpConfigForm({
    form,
    hasPassword,
    loading,
    onTest,
    onUpdate,
    onSubmit,
    saving,
    t,
    testing,
}) {
    if (loading) {
        return <div className="smtp-empty">{t("state.loading", { ns: "common" })}</div>;
    }

    return (
        <div className="smtp-config">
            <p className="smtp-settings__hint">
                {t(
                    "smtp.hint",
                    "Данные почтового сервера для отправки документов пациентам: УЗИ, анкеты, план лечения. Без этих настроек кнопка «Отправить на почту» неактивна."
                )}
            </p>

            <div className="smtp-grid">
                <label className="smtp-field">
                    <span>{t("smtp.fields.host", "SMTP-сервер")}</span>
                    <input
                        className="form-control"
                        onChange={onUpdate("host")}
                        placeholder="smtp.yandex.ru"
                        type="text"
                        value={form.host}
                    />
                </label>
                <label className="smtp-field">
                    <span>{t("smtp.fields.port", "Порт")}</span>
                    <input
                        className="form-control"
                        inputMode="numeric"
                        onChange={onUpdate("port")}
                        placeholder="465"
                        type="text"
                        value={form.port}
                    />
                </label>
                <label className="smtp-field">
                    <span>{t("smtp.fields.secure", "Шифрование")}</span>
                    <select className="form-control" onChange={onUpdate("secure")} value={form.secure}>
                        <option value="ssl">{t("smtp.secure.ssl", "SSL (обычно 465)")}</option>
                        <option value="tls">{t("smtp.secure.tls", "STARTTLS (обычно 587)")}</option>
                        <option value="none">{t("smtp.secure.none", "Без шифрования")}</option>
                    </select>
                </label>
                <label className="smtp-field">
                    <span>{t("smtp.fields.user", "Логин")}</span>
                    <input
                        className="form-control"
                        onChange={onUpdate("user")}
                        placeholder="user@example.ru"
                        type="text"
                        value={form.user}
                    />
                </label>
                <label className="smtp-field">
                    <span>
                        {t("smtp.fields.password", "Пароль")}
                        {hasPassword
                            ? ` — ${t("smtp.passwordSet", "задан, оставьте пустым, чтобы не менять")}`
                            : ""}
                    </span>
                    <input
                        autoComplete="new-password"
                        className="form-control"
                        onChange={onUpdate("password")}
                        placeholder={
                            hasPassword
                                ? "••••••••"
                                : t("smtp.passwordPlaceholder", "пароль или пароль приложения")
                        }
                        type="password"
                        value={form.password}
                    />
                </label>
                <label className="smtp-field">
                    <span>{t("smtp.fields.fromEmail", "Адрес отправителя")}</span>
                    <input
                        className="form-control"
                        onChange={onUpdate("from_email")}
                        placeholder="clinic@example.ru"
                        type="text"
                        value={form.from_email}
                    />
                </label>
                <label className="smtp-field">
                    <span>{t("smtp.fields.fromName", "Имя отправителя")}</span>
                    <input
                        className="form-control"
                        onChange={onUpdate("from_name")}
                        placeholder="Клиника РЕФРЕШ"
                        type="text"
                        value={form.from_name}
                    />
                </label>
            </div>

            <div className="smtp-actions">
                <Button disabled={saving} loading={saving} onClick={onSubmit} variant="primary">
                    {saving ? t("smtp.saving", "Сохраняем…") : t("smtp.save", "Сохранить")}
                </Button>
                <Button disabled={testing} loading={testing} onClick={onTest} variant="secondary">
                    {testing ? t("smtp.testing", "Проверяем…") : t("smtp.test", "Проверить отправку")}
                </Button>
            </div>
        </div>
    );
}

/** Таб «Журнал»: история отправленных писем (email_log). */
function SmtpLog({ emails, loading, onRefresh, t }) {
    return (
        <div className="smtp-log">
            <div className="smtp-log-head">
                <span className="smtp-log-title">{t("smtp.log.title", "Отправленные письма")}</span>
                <Button onClick={onRefresh} size="sm" variant="secondary">
                    {t("smtp.log.refresh", "Обновить")}
                </Button>
            </div>

            {loading ? (
                <div className="smtp-empty">{t("state.loading", { ns: "common" })}</div>
            ) : emails.length === 0 ? (
                <div className="no-patients">{t("smtp.log.empty", "Писем ещё не отправляли")}</div>
            ) : (
                <div className="smtp-log-scroll">
                    <Table className="main-table-list rounded-table smtp-log-table">
                        <thead>
                            <tr>
                                <th className="smtp-log-col-date">{t("smtp.log.colDate", "Дата")}</th>
                                <th className="smtp-log-col-patient">{t("smtp.log.colPatient", "Пациент")}</th>
                                <th className="smtp-log-col-to">{t("smtp.log.colTo", "Кому")}</th>
                                <th className="smtp-log-col-subject">{t("smtp.log.colSubject", "Тема")}</th>
                                <th className="smtp-log-col-attach">
                                    {t("smtp.log.colAttachment", "Вложение")}
                                </th>
                                <th className="smtp-log-col-user">{t("smtp.log.colUser", "Отправил")}</th>
                                <th className="smtp-log-col-status">
                                    {t("smtp.log.colStatus", "Статус")}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {emails.map((row) => {
                                const attachment = row.link
                                    ? t("smtp.log.link", "ссылка")
                                    : row.attachment_name
                                      ? `${row.attachment_name} (${Math.max(
                                            1,
                                            Math.round((row.attachment_size || 0) / 1024)
                                        )} КБ)`
                                      : "—";
                                return (
                                    <tr key={row.id}>
                                        <td className="smtp-log-date">{row.date || "—"}</td>
                                        <td
                                            className="smtp-log-ellipsis"
                                            title={row.patient || ""}>
                                            {row.patient || "—"}
                                        </td>
                                        <td
                                            className="smtp-log-ellipsis"
                                            title={row.to_email || ""}>
                                            {row.to_email || "—"}
                                        </td>
                                        <td
                                            className="smtp-log-ellipsis"
                                            title={row.subject || ""}>
                                            {row.subject || "—"}
                                        </td>
                                        <td
                                            className="smtp-log-ellipsis smtp-log-attach"
                                            title={row.link || attachment}>
                                            {row.link ? (
                                                <a
                                                    className="smtp-log-link"
                                                    href={row.link}
                                                    rel="noreferrer noopener"
                                                    target="_blank">
                                                    {attachment}
                                                </a>
                                            ) : (
                                                attachment
                                            )}
                                        </td>
                                        <td
                                            className="smtp-log-ellipsis"
                                            title={row.user || ""}>
                                            {row.user || "—"}
                                        </td>
                                        <td className="smtp-log-col-status">
                                            {row.status === "success" ? (
                                                <span className="smtp-log-status smtp-log-status--ok">
                                                    {t("smtp.log.sent", "отправлено")}
                                                </span>
                                            ) : (
                                                <span
                                                    className="smtp-log-status smtp-log-status--fail"
                                                    title={row.error || ""}>
                                                    {t("smtp.log.failed", "ошибка")}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    );
}
